/**
 * Vertical configuration packages.
 *
 * A vertical is NOT a fork of the product. It is a configuration that adjusts:
 *   - The signal dictionary (which events matter)
 *   - The play library (which channels/templates are standard)
 *   - The value model (how $ is computed)
 *   - The lifecycle assumptions (recurring vs one-time)
 *
 * To ship a new vertical, add an entry below. No component code should change.
 */

/**
 * @typedef {Object} VerticalPackage
 * @property {import('../contracts').VerticalId} id
 * @property {string} label
 * @property {"recurring"|"one_time"|"mixed"} lifecycle
 * @property {string[]} typicalSegments
 * @property {import('../contracts').PlayChannel[]} enabledChannels
 * @property {string[]} keySignals
 */

/** @type {VerticalPackage[]} */
export const VERTICALS = [
  {
    id: "software_distributors",
    label: "Distribuidores de software",
    lifecycle: "recurring",
    typicalSegments: ["Enterprise", "Mid-Market", "SMB", "Corporativo"],
    enabledChannels: ["call", "email", "whatsapp", "meeting", "proposal"],
    keySignals: [
      "email_opened",
      "pricing_page_visit",
      "renewal_expired",
      "docs_downloaded",
      "inactivity_90d",
    ],
  },
  {
    id: "agencies",
    label: "Agencias",
    lifecycle: "recurring",
    typicalSegments: ["Retainer", "Proyecto", "Performance"],
    enabledChannels: ["call", "email", "whatsapp", "meeting", "proposal"],
    keySignals: ["retainer_ending", "brief_received", "campaign_completed"],
  },
  {
    id: "consultants",
    label: "Consultores",
    lifecycle: "mixed",
    typicalSegments: ["Estratégico", "Ejecución", "Advisory"],
    enabledChannels: ["call", "email", "meeting", "proposal"],
    keySignals: ["engagement_ending", "referral_signal", "expansion_signal"],
  },
  {
    id: "coaches",
    label: "Coaches",
    lifecycle: "mixed",
    typicalSegments: ["1:1", "Grupal", "Programa"],
    enabledChannels: ["call", "email", "whatsapp", "meeting"],
    keySignals: ["program_ending", "milestone_reached", "renewal_window"],
  },
  {
    id: "info_products",
    label: "Infoproductores",
    lifecycle: "one_time",
    typicalSegments: ["Front-end", "Core", "High-Ticket"],
    enabledChannels: ["email", "whatsapp"],
    keySignals: ["cart_abandoned", "webinar_attended", "upsell_page_visited"],
  },
  {
    id: "insurance",
    label: "Aseguradoras",
    lifecycle: "recurring",
    typicalSegments: ["Vida", "Autos", "Salud", "Empresas"],
    enabledChannels: ["call", "whatsapp", "visit", "meeting"],
    keySignals: ["policy_renewal_60d", "premium_overdue", "claim_filed"],
  },
  {
    id: "real_estate",
    label: "Inmobiliarias",
    lifecycle: "one_time",
    typicalSegments: ["Residencial", "Comercial", "Inversión"],
    enabledChannels: ["call", "whatsapp", "visit", "meeting"],
    keySignals: ["listing_view", "visit_scheduled", "offer_stalled"],
  },
  {
    id: "accounting",
    label: "Despachos contables",
    lifecycle: "recurring",
    typicalSegments: ["Persona física", "Pyme", "Grupo"],
    enabledChannels: ["call", "email", "whatsapp", "meeting"],
    keySignals: ["fiscal_deadline", "invoice_upload", "engagement_gap"],
  },
  {
    id: "clinics",
    label: "Clínicas",
    lifecycle: "recurring",
    typicalSegments: ["Paciente activo", "Recurrente", "Prospecto"],
    enabledChannels: ["call", "whatsapp", "email"],
    keySignals: ["visit_gap", "treatment_completed", "referral_pending"],
  },
  {
    id: "services",
    label: "Empresas de servicios",
    lifecycle: "mixed",
    typicalSegments: ["Contrato", "Bolsa de horas", "Proyecto"],
    enabledChannels: ["call", "email", "meeting", "proposal"],
    keySignals: ["hours_depleted", "contract_ending", "milestone_delivered"],
  },
  {
    id: "manufacturers",
    label: "Fabricantes",
    lifecycle: "recurring",
    typicalSegments: ["OEM", "Distribuidor", "Directo"],
    enabledChannels: ["call", "email", "meeting", "visit"],
    keySignals: ["order_cadence_break", "stock_alert", "quote_pending"],
  },
  {
    id: "wholesalers",
    label: "Mayoristas",
    lifecycle: "recurring",
    typicalSegments: ["Distribuidor", "Retail", "Corporativo"],
    enabledChannels: ["call", "whatsapp", "email", "visit"],
    keySignals: ["order_gap", "seasonal_window", "credit_limit_available"],
  },
];

/** @param {import('../contracts').VerticalId} id */
export const getVertical = (id) => VERTICALS.find((v) => v.id === id);

/**
 * Vertical del tenant activo — env-driven (mismo patrón que
 * DEFAULT_ORGANIZATION_ID abajo). "software_distributors" es el fallback
 * solo porque es el vertical validado hasta ahora; un despliegue nuevo con
 * otro tipo de negocio fija REACT_APP_VERTICAL_ID, cero código.
 */
export const DEFAULT_VERTICAL_ID =
  process.env.REACT_APP_VERTICAL_ID || "software_distributors";

/**
 * Tenant actual de este despliegue. Env-driven a propósito (mismo patrón que
 * REACT_APP_*_ADAPTER en ports-config.js): cada despliegue de un socio nuevo
 * (Guillermo, etc.) solo necesita fijar REACT_APP_ORGANIZATION_ID — cero
 * código, cero nombres de socios hardcodeados en archivos compartidos.
 */
export const DEFAULT_ORGANIZATION_ID =
  process.env.REACT_APP_ORGANIZATION_ID || "default-organization";

/**
 * Nombre visible de la organización activa — lo que ve el usuario en la
 * barra superior, nunca el vertical. Sin nombre de socio hardcodeado aquí:
 * cada despliegue fija el suyo por variable de entorno.
 */
export const DEFAULT_ORGANIZATION_NAME =
  process.env.REACT_APP_ORGANIZATION_NAME || "Mi organización";

/**
 * Nombre de pila del Socio Fundador — usado únicamente en la bienvenida de
 * primer ingreso (pages/Welcome.jsx). Mismo patrón env-driven: cada
 * despliegue fija el suyo, cero nombres hardcodeados en código compartido.
 */
export const FOUNDER_FIRST_NAME =
  process.env.REACT_APP_FOUNDER_FIRST_NAME || "";

/**
 * Nombre completo del Socio Fundador — usado en la identidad persistente de
 * la barra superior (components/layout/TopBar.jsx). Sin esto, la esquina
 * superior derecha muestra "Director Comercial" — el nombre genérico del
 * operador interno de laboratorio, nunca el nombre real de quien está
 * usando el producto. Cada despliegue de un Socio Fundador fija el suyo.
 */
export const FOUNDER_FULL_NAME =
  process.env.REACT_APP_FOUNDER_FULL_NAME || "";

/**
 * Distintivo opcional del Socio Fundador (ej. "Socio Fundador #001") — solo
 * para la pantalla de entrega previa (pages/FounderHandoff.jsx). Vacío por
 * default: si un despliegue no lo fija, simplemente no se muestra ningún
 * distintivo, nunca un placeholder inventado.
 */
export const FOUNDER_BADGE =
  process.env.REACT_APP_FOUNDER_BADGE || "";
